import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Loading } from "@/components/atoms";
import { AccountType } from "@/constants/accountTypes";
import { getPhpHostUrl } from "@/helpers/apiHelpers";
import { useLazyGetAccountsQuery } from "@/redux/apis/account/accountApi";
import { useLazyGetProfileQuery } from "@/redux/apis/profile/profileApi";
import { useAppDispatch } from "@/redux/hooks";
import { setActiveAccount } from "@/redux/slices/account/accountSlice";

// Type definitions for Kinde claims
type UserProperty = { v: string };
type ClaimsWithUserProperties = {
  user_properties?: {
    kp_usr_utm_content?: UserProperty;
    kp_usr_utm_medium?: UserProperty;
    kp_usr_utm_term?: UserProperty;
  };
};

/**
 * AuthCallback — handles Kinde redirect (after RegisterLink or LoginLink)
 * Unprotected route that completes the OAuth flow before redirecting
 *
 * Flow Logic:
 *
 * PRIORITY ORDER FOR USER TYPE DETECTION:
 * 1. Check profile.accountMetadata.type (most reliable, stored in database)
 * 2. Fallback to Kinde claims utm_content (for new users or if metadata missing)
 * 3. Final fallback: redirect to /onboarding/error if registration incomplete, or dashboard if complete
 *
 * WELCOME OVERLAY:
 * - Uses sessionStorage flag "justCompletedOnboarding" to track first-time registrations
 * - Flag is set when redirecting TO onboarding, checked when returning FROM onboarding
 * - Private.tsx detects the flag and shows welcome overlay using user type from profile/Kinde claims
 * - This works across both dev (direct React navigation) and production (PHP redirect) environments
 *
 * DRIVER FLOW (type === "driver"):
 * 1. Driver attempts to log in via Kinde
 * 2. After Kinde authentication, redirected here
 * 3. Driver type detected from profile.accountMetadata.type
 * 4. Immediately redirect to /driver-app (mobile app download page)
 * 5. Drivers cannot access any other web app routes - they must use the mobile app
 *
 * OPERATOR FLOW (type === "operator"):
 * 1. User registers via /dashboard/signup/operator
 * 2. After Kinde registration, redirected here
 * 3. Fetch fresh account data and check profile.accountMetadata.isRegisterComplete
 * 4. If isRegisterComplete === true → login to PHP site & redirect to dashboard
 * 5. If isRegisterComplete === false → set sessionStorage flag, redirect to "/onboarding/operator"
 * 6. After onboarding completes → redirect back here → login to PHP site → Private.tsx shows overlay
 *
 * CLIENT FLOW (type === "passenger" or "executive_assistant"):
 * 1. User selects user type (passenger/EA) at /dashboard/signup/:providerCode
 * 2. After Kinde registration, redirected here
 * 3. Check profile.accountMetadata.isRegisterComplete
 * 4. If isRegisterComplete === true → login to PHP site with providerCode
 * 5. If isRegisterComplete === false → set sessionStorage flag, redirect to "/onboarding/client"
 * 6. After onboarding completes → redirect back here → login to PHP site → Private.tsx shows overlay
 *
 * USER ACCOUNT FLOW (type === "user_account"):
 * 1. User clicks invite link from email → /dashboard/invite/{inviteKey} (full URL with basename)
 * 2. UserAccountInvite page redirects to Kinde signup with utm_content: "user_account" and utm_term: inviteKey
 * 3. After Kinde registration, redirected here
 * 4. If isRegisterComplete === true → login to PHP site & redirect to dashboard
 * 5. If isRegisterComplete === false → set sessionStorage flag, redirect to /onboarding/invite/{inviteKey}
 * 6. After onboarding completes → redirect back here → login to PHP site → Private.tsx shows overlay
 *
 * Note: Always fetches fresh data from APIs to ensure up-to-date state after Kinde auth.
 * Uses ref to prevent multiple executions of the auth flow.
 */
export const AuthCallback = () => {
  const { isAuthenticated, isLoading, getClaims, getToken } = useKindeAuth();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [getAccounts] = useLazyGetAccountsQuery();
  const [getProfile] = useLazyGetProfileQuery();

  // Use ref to ensure the effect only runs once per mount
  const hasRun = useRef(false);
  // Store utm_content for error handling
  const utmContentRef = useRef<string | undefined>();
  // Refs for PHP form login
  const tokenPhpRef = useRef<HTMLInputElement>(null);
  const submitPhpRef = useRef<HTMLInputElement>(null);
  const formActionRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    // Prevent multiple executions
    if (hasRun.current) return;

    // Wait for Kinde to finish hydrating
    if (isLoading) return;

    // Not authenticated → redirect to login
    if (!isAuthenticated) {
      navigate("/login", { replace: true });
      return;
    }

    // Mark as run to prevent re-execution
    hasRun.current = true;

    /**
     * Logs the user into the old PHP website using Kinde access token
     * ONLY works in production - caller must handle dev mode separately
     * @param providerCode - Required for clients to set the correct PHP host
     * @param shouldRedirectToDashboard - Whether to redirect back to React dashboard (true for operators, false for clients)
     */
    const loginToPhpSite = async (providerCode?: string, shouldRedirectToDashboard: boolean = true) => {
      try {
        const token = await getToken();
        if (token && tokenPhpRef.current && submitPhpRef.current && formActionRef.current) {
          const phpHost = providerCode ? getPhpHostUrl(providerCode) : getPhpHostUrl();
          const redirectParam = shouldRedirectToDashboard ? `&redirect=/dashboard` : "";
          formActionRef.current.action = `${phpHost}/login?referrer=dashboard${redirectParam}`;

          tokenPhpRef.current.value = token;
          submitPhpRef.current.click();
        }
      } catch {
        console.error("Failed to login to PHP site");
      }
    };

    /**
     * Determines where to route the user based on account metadata type and registration completion status
     * Falls back to utm_content from Kinde claims only if metadata is missing
     */
    const handleRouting = (
      accountType: string | undefined,
      isRegisterComplete: boolean,
      providerCode?: string,
      utmContentFallback?: string,
      inviteKey?: string
    ) => {
      // Determine user type: prioritize account metadata, fallback to Kinde claims
      const userType = accountType || utmContentFallback;

      // DRIVER FLOW - Drivers should use mobile app
      if (userType === "driver") {
        navigate("/driver-app", { replace: true });
        return;
      }

      // USER ACCOUNT FLOW (invited team members: Admin, Manager, Dispatcher, Call Center)
      if (userType === "user_account") {
        if (isRegisterComplete) {
          // Registration complete → go to dashboard
          if (import.meta.env.MODE !== "production") {
            navigate("/", { replace: true });
          } else {
            loginToPhpSite(providerCode, true);
          }
        } else if (inviteKey) {
          // Registration incomplete → redirect to onboarding page
          // Set flag to show welcome overlay after onboarding completes
          sessionStorage.setItem("justCompletedOnboarding", "true");
          // Note: Don't include /dashboard - it's the basename
          navigate(`/onboarding/invite/${inviteKey}`, { replace: true });
        } else {
          console.error("No invite key found for user_account (utm_term missing from Kinde claims)");
          navigate("/404", { replace: true });
        }
        return;
      }

      // OPERATOR FLOW
      if (userType === "operator") {
        if (isRegisterComplete) {
          // Registration complete → go to dashboard
          // Note: Don't remove sessionStorage flag here, Private.tsx will handle it
          if (import.meta.env.MODE !== "production") {
            navigate("/", { replace: true });
          } else {
            loginToPhpSite(undefined, true);
          }
        } else {
          // Registration incomplete → redirect to onboarding page
          // Set flag to show welcome overlay after onboarding completes
          sessionStorage.setItem("justCompletedOnboarding", "true");
          navigate("/onboarding/operator", { replace: true });
        }
        return;
      }

      // CLIENT FLOW (passenger, executive_assistant, travel_manager, "ea" for backwards compat, or "client" from old claims)
      const clientTypes = [AccountType.PASSENGER, AccountType.EXECUTIVE_ASSISTANT, AccountType.TRAVEL_MANAGER, "ea", "client"];
      if (clientTypes.includes(userType as string)) {
        if (isRegisterComplete) {
          // Registration complete → go to dashboard
          // Note: Don't remove sessionStorage flag here, Private.tsx will handle it
          if (import.meta.env.MODE !== "production") {
            navigate("/", { replace: true });
            return;
          }

          if (!providerCode) {
            console.error("No provider code found for client");
            navigate("/404", { replace: true });
            return;
          }

          loginToPhpSite(providerCode, false);
        } else {
          // Registration incomplete → redirect to onboarding page
          // Set flag to show welcome overlay after onboarding completes
          sessionStorage.setItem("justCompletedOnboarding", "true");
          navigate("/onboarding/client", { replace: true });
        }
        return;
      }

      // FALLBACK: Unknown user type
      console.error("Auth Callback - Unknown user type", {
        accountType,
        utmContentFallback,
      });

      if (isRegisterComplete) {
        // Registration complete → go to dashboard
        if (import.meta.env.MODE !== "production") {
          navigate("/", { replace: true });
        } else {
          loginToPhpSite(undefined, true);
        }
      } else {
        // Registration incomplete with unknown type → redirect to error page
        navigate("/onboarding/error", { replace: true });
      }
    };

    // Fetch account and profile data first, then check claims as fallback
    getAccounts()
      .unwrap()
      .then((res) => {
        const accounts = res.data.accounts;
        const primaryAccount = accounts.find((account) => account.primary);

        if (!primaryAccount) {
          console.error("No primary account found");
          navigate("/onboarding/error", { replace: true });
          return;
        }

        dispatch(setActiveAccount(primaryAccount));

        return getProfile()
          .unwrap()
          .then((profile) => {
            const isRegisterComplete = profile.accountMetadata?.isRegisterComplete ?? false;
            const accountType = profile.accountMetadata?.type;
            const providerCode = profile.accountMetadata?.providerCode;

            // If we have account metadata type, use it directly
            if (accountType) {
              handleRouting(accountType, isRegisterComplete, providerCode, undefined);
              return;
            }

            // Fallback: fetch Kinde claims if account metadata is missing
            return getClaims("idToken")
              .then((claims) => {
                const typedClaims = claims as ClaimsWithUserProperties;
                const utmContent = typedClaims?.user_properties?.kp_usr_utm_content?.v;
                const utmTerm = typedClaims?.user_properties?.kp_usr_utm_term?.v;

                utmContentRef.current = utmContent;
                handleRouting(undefined, isRegisterComplete, providerCode, utmContent, utmTerm);
              })
              .catch(() => {
                console.error("Failed to fetch Kinde claims");
                // No metadata and no claims - fallback to unknown type handling
                handleRouting(undefined, isRegisterComplete, providerCode, undefined, undefined);
              });
          });
      })
      .catch(() => {
        console.error("Failed to fetch account or profile data");
        // Try to determine user type from cached claims
        getClaims("idToken")
          .then((claims) => {
            const typedClaims = claims as ClaimsWithUserProperties;
            const utmContent = typedClaims?.user_properties?.kp_usr_utm_content?.v;
            const utmTerm = typedClaims?.user_properties?.kp_usr_utm_term?.v;
            utmContentRef.current = utmContent;

            if (utmContent === "user_account") {
              // For user_account, redirect to onboarding page
              // Note: Don't include /dashboard - it's the basename
              if (utmTerm) {
                navigate(`/onboarding/invite/${utmTerm}`, { replace: true });
              } else {
                console.error("No invite key found for user_account (utm_term missing from Kinde claims)");
                navigate("/404", { replace: true });
              }
            } else if (utmContent === "client") {
              navigate("/404", { replace: true });
            } else if (utmContent === "operator") {
              navigate("/onboarding/operator", { replace: true });
            } else {
              navigate("/onboarding/error", { replace: true });
            }
          })
          .catch(() => {
            // Can't determine user type, redirect to error page
            navigate("/onboarding/error", { replace: true });
          });
      });
  }, [isAuthenticated, isLoading, getAccounts, getProfile, dispatch, navigate, getClaims, getToken]);

  return (
    <>
      <Loading />
      <form id="phpForm" ref={formActionRef} method="post" action="" style={{ display: "none" }}>
        <input type="hidden" name="token" ref={tokenPhpRef} />
        <input type="submit" ref={submitPhpRef} />
      </form>
    </>
  );
};
